import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import {
  Brain,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Search,
  Plus,
  Trash2,
  RefreshCw,
  Filter,
  ChevronDown,
  ChevronUp,
  Eye,
  FileText,
  Settings,
  Zap,
  Target,
  AlertCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const CLASSIFICATION_TYPES = [
  { value: 'account', label: 'Account', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'inquiry', label: 'Inquiry', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'public_record', label: 'Public Record', color: 'bg-amber-100 text-amber-700' },
  { value: 'address', label: 'Address', color: 'bg-slate-100 text-slate-700' },
  { value: 'personal_info', label: 'Personal Info', color: 'bg-slate-100 text-slate-700' },
  { value: 'employment', label: 'Employment', color: 'bg-slate-100 text-slate-700' },
  { value: 'section_header', label: 'Section Header', color: 'bg-amber-100 text-amber-700' },
  { value: 'summary_text', label: 'Summary Text', color: 'bg-amber-100 text-amber-700' },
  { value: 'noise', label: 'Noise/Discard', color: 'bg-red-100 text-red-700' },
  { value: 'unknown', label: 'Unknown', color: 'bg-orange-100 text-orange-700' },
];

const getClassBadge = (classification) => {
  const classType = CLASSIFICATION_TYPES.find(c => c.value === classification);
  return classType ? (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${classType.color}`}>
      {classType.label}
    </span>
  ) : (
    <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
      {classification}
    </span>
  );
};

// Review Queue Component
const ReviewQueue = ({ getAuthHeaders }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedItem, setExpandedItem] = useState(null);

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API}/admin/parser/review-queue`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setItems(response.data.items || []);
    } catch (error) {
      console.error('Failed to fetch review queue:', error);
      toast.error('Failed to load review queue');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (itemId, action, finalClass = null) => {
    try {
      await axios.post(`${API}/admin/parser/review-queue/${itemId}/action`, 
        { action, final_class: finalClass },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      toast.success(`Item ${action}d`);
      fetchItems();
    } catch (error) {
      toast.error('Action failed');
    }
  };

  if (loading) {
    return <div className="space-y-4">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-24" />)}</div>;
  }

  if (items.length === 0) {
    return (
      <div className="text-center py-12 bg-slate-50 rounded-xl">
        <CheckCircle size={48} className="mx-auto text-emerald-500 mb-4" />
        <h3 className="text-lg font-semibold text-stone-900">Review Queue Empty</h3>
        <p className="text-slate-500">No items pending review</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{items.length} items pending review</p>
        <Button variant="outline" size="sm" onClick={fetchItems}>
          <RefreshCw size={14} className="mr-2" />
          Refresh
        </Button>
      </div>
      
      {items.map((item) => (
        <div key={item.item_id} className="elite-card p-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                {getClassBadge(item.suggested_class)}
                <span className={`text-xs px-2 py-0.5 rounded ${
                  item.confidence >= 0.6 ? 'bg-emerald-100 text-emerald-700' :
                  item.confidence >= 0.3 ? 'bg-amber-100 text-amber-700' :
                  'bg-red-100 text-red-700'
                }`}>
                  {Math.round(item.confidence * 100)}% confidence
                </span>
              </div>
              
              <p className="text-sm text-slate-700 font-mono bg-slate-50 p-2 rounded mb-2 max-h-20 overflow-y-auto">
                {item.raw_text?.substring(0, 200)}...
              </p>
              
              <button 
                className="text-xs text-amber-600 hover:underline flex items-center gap-1"
                onClick={() => setExpandedItem(expandedItem === item.item_id ? null : item.item_id)}
              >
                {expandedItem === item.item_id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                {expandedItem === item.item_id ? 'Hide details' : 'Show details'}
              </button>
              
              {expandedItem === item.item_id && (
                <div className="mt-3 p-3 bg-slate-50 rounded-lg text-xs space-y-2">
                  <div>
                    <strong>Parsed Fields:</strong>
                    <pre className="mt-1 p-2 bg-white rounded text-[10px] overflow-x-auto">
                      {JSON.stringify(item.parsed_fields, null, 2)}
                    </pre>
                  </div>
                  <div>
                    <strong>Signals Found:</strong> {item.signals_found?.join(', ') || 'None'}
                  </div>
                  <div>
                    <strong>Exclusions Found:</strong> {item.exclusions_found?.join(', ') || 'None'}
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex flex-col gap-2 ml-4">
              <Button 
                size="sm" 
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={() => handleAction(item.item_id, 'approve')}
              >
                <CheckCircle size={14} className="mr-1" />
                Account
              </Button>
              <Button 
                size="sm" 
                variant="outline"
                className="border-red-300 text-red-600 hover:bg-red-50"
                onClick={() => handleAction(item.item_id, 'reject')}
              >
                <XCircle size={14} className="mr-1" />
                Not Account
              </Button>
              <Select onValueChange={(v) => handleAction(item.item_id, 'reclassify', v)}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Reclassify..." />
                </SelectTrigger>
                <SelectContent>
                  {CLASSIFICATION_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Rules Management Component
const RulesManager = ({ getAuthHeaders }) => {
  const [rules, setRules] = useState([]);
  const [defaultExclusions, setDefaultExclusions] = useState([]);
  const [defaultInclusions, setDefaultInclusions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddRule, setShowAddRule] = useState(false);
  const [newRule, setNewRule] = useState({
    rule_type: 'exclusion',
    pattern: '',
    target_class: 'noise',
    provider: '',
    priority: 0
  });

  useEffect(() => {
    fetchRules();
  }, []);

  const fetchRules = async () => {
    try {
      const response = await axios.get(`${API}/admin/parser/rules`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setRules(response.data.rules || []);
      setDefaultExclusions(response.data.default_exclusions || []);
      setDefaultInclusions(response.data.default_inclusions || []);
    } catch (error) {
      console.error('Failed to fetch rules:', error);
    } finally {
      setLoading(false);
    }
  };

  const createRule = async () => {
    if (!newRule.pattern.trim()) {
      toast.error('Pattern is required');
      return;
    }
    
    try {
      await axios.post(`${API}/admin/parser/rules`, newRule, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success('Rule created');
      setShowAddRule(false);
      setNewRule({ rule_type: 'exclusion', pattern: '', target_class: 'noise', provider: '', priority: 0 });
      fetchRules();
    } catch (error) {
      toast.error('Failed to create rule');
    }
  };

  const deleteRule = async (ruleId) => {
    try {
      await axios.delete(`${API}/admin/parser/rules/${ruleId}`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success('Rule deleted');
      fetchRules();
    } catch (error) {
      toast.error('Failed to delete rule');
    }
  };

  if (loading) {
    return <Skeleton className="h-64" />;
  }

  return (
    <div className="space-y-6">
      {/* Custom Rules */}
      <div className="elite-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-stone-900">Custom Rules</h3>
          <Button size="sm" onClick={() => setShowAddRule(true)}>
            <Plus size={14} className="mr-2" />
            Add Rule
          </Button>
        </div>
        
        {rules.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-8">No custom rules defined</p>
        ) : (
          <div className="space-y-2">
            {rules.map(rule => (
              <div key={rule.rule_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <span className={`px-2 py-0.5 text-xs rounded ${
                    rule.rule_type === 'exclusion' ? 'bg-red-100 text-red-700' :
                    rule.rule_type === 'inclusion' ? 'bg-emerald-100 text-emerald-700' :
                    'bg-amber-100 text-amber-700'
                  }`}>
                    {rule.rule_type}
                  </span>
                  <code className="text-sm bg-white px-2 py-1 rounded">{rule.pattern}</code>
                  <span className="text-xs text-slate-500">→ {rule.target_class}</span>
                  {rule.provider && <span className="text-xs text-slate-400">({rule.provider})</span>}
                </div>
                <Button variant="ghost" size="sm" onClick={() => deleteRule(rule.rule_id)}>
                  <Trash2 size={14} className="text-red-500" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Default Terms */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="elite-card p-6">
          <h3 className="font-semibold text-stone-900 mb-4 flex items-center gap-2">
            <XCircle size={18} className="text-red-500" />
            Non-Account Terms (Built-in)
          </h3>
          <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto">
            {defaultExclusions.map((term, i) => (
              <span key={i} className="px-2 py-1 text-xs bg-red-50 text-red-700 rounded">
                {term}
              </span>
            ))}
          </div>
        </div>
        
        <div className="elite-card p-6">
          <h3 className="font-semibold text-stone-900 mb-4 flex items-center gap-2">
            <CheckCircle size={18} className="text-emerald-500" />
            Account Signals (Built-in)
          </h3>
          <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto">
            {defaultInclusions.map((term, i) => (
              <span key={i} className="px-2 py-1 text-xs bg-emerald-50 text-emerald-700 rounded">
                {term}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Add Rule Dialog */}
      <Dialog open={showAddRule} onOpenChange={setShowAddRule}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Parser Rule</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Rule Type</Label>
              <Select value={newRule.rule_type} onValueChange={(v) => setNewRule({...newRule, rule_type: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="exclusion">Exclusion (not an account)</SelectItem>
                  <SelectItem value="inclusion">Inclusion (is an account)</SelectItem>
                  <SelectItem value="classification">Classification (specific type)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Pattern (keyword or regex)</Label>
              <Input 
                value={newRule.pattern}
                onChange={(e) => setNewRule({...newRule, pattern: e.target.value})}
                placeholder="e.g., 'promotional inquiry' or '/\d{3}-\d{2}-\d{4}/'"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Target Classification</Label>
              <Select value={newRule.target_class} onValueChange={(v) => setNewRule({...newRule, target_class: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CLASSIFICATION_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Provider (optional)</Label>
              <Input 
                value={newRule.provider}
                onChange={(e) => setNewRule({...newRule, provider: e.target.value})}
                placeholder="e.g., IdentityIQ, SmartCredit"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddRule(false)}>Cancel</Button>
            <Button onClick={createRule}>Create Rule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Test Classifier Component  
const ClassifierTester = ({ getAuthHeaders }) => {
  const [testText, setTestText] = useState('');
  const [result, setResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const runTest = async () => {
    if (!testText.trim()) {
      toast.error('Enter text to test');
      return;
    }
    
    setTesting(true);
    try {
      const response = await axios.post(`${API}/admin/parser/test-classification`,
        { text: testText },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      setResult(response.data);
    } catch (error) {
      toast.error('Test failed');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="elite-card p-6">
      <h3 className="font-semibold text-stone-900 mb-4 flex items-center gap-2">
        <Zap size={18} className="text-amber-500" />
        Test Classifier
      </h3>
      
      <div className="space-y-4">
        <Textarea
          value={testText}
          onChange={(e) => setTestText(e.target.value)}
          placeholder="Paste text from a credit report to test classification..."
          rows={5}
        />
        
        <Button onClick={runTest} disabled={testing}>
          {testing ? 'Testing...' : 'Test Classification'}
        </Button>
        
        {result && (
          <div className="mt-4 p-4 bg-slate-50 rounded-lg">
            <h4 className="font-semibold mb-3">Result:</h4>
            
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <p className="text-xs text-slate-500 mb-1">Classification</p>
                {getClassBadge(result.classification?.classification)}
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Confidence</p>
                <span className="font-semibold">{Math.round((result.classification?.confidence || 0) * 100)}%</span>
              </div>
            </div>
            
            <div className="space-y-2 text-sm">
              <p><strong>Valid Account:</strong> {result.classification?.is_valid_account ? 'Yes' : 'No'}</p>
              <p><strong>Needs Review:</strong> {result.classification?.needs_review ? 'Yes' : 'No'}</p>
              <p><strong>Reason:</strong> {result.classification?.reason}</p>
              
              {result.classification?.signals_found?.length > 0 && (
                <div>
                  <strong>Signals Found:</strong>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {result.classification.signals_found.map((s, i) => (
                      <span key={i} className="px-2 py-0.5 text-xs bg-emerald-100 text-emerald-700 rounded">{s}</span>
                    ))}
                  </div>
                </div>
              )}
              
              {result.classification?.exclusions_found?.length > 0 && (
                <div>
                  <strong>Exclusions Found:</strong>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {result.classification.exclusions_found.map((s, i) => (
                      <span key={i} className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded">{s}</span>
                    ))}
                  </div>
                </div>
              )}
              
              {result.validation && (
                <div className="mt-3 pt-3 border-t">
                  <p><strong>Validation:</strong> {result.validation.is_valid ? '✓ Valid' : '✗ Invalid'}</p>
                  {result.validation.issues?.length > 0 && (
                    <ul className="list-disc list-inside text-red-600 text-xs mt-1">
                      {result.validation.issues.map((issue, i) => <li key={i}>{issue}</li>)}
                    </ul>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Training Feedback Component
const TrainingFeedback = ({ getAuthHeaders }) => {
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});

  useEffect(() => {
    fetchFeedback();
  }, []);

  const fetchFeedback = async () => {
    try {
      const response = await axios.get(`${API}/admin/parser/feedback`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setFeedback(response.data.feedback || []);
      setStats(response.data.by_class || {});
    } catch (error) {
      console.error('Failed to fetch feedback:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Skeleton className="h-64" />;
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="elite-card p-4 text-center">
          <p className="text-2xl font-bold text-emerald-600">{stats.account || 0}</p>
          <p className="text-xs text-slate-500">Marked as Account</p>
        </div>
        <div className="elite-card p-4 text-center">
          <p className="text-2xl font-bold text-red-600">{stats.noise || 0}</p>
          <p className="text-xs text-slate-500">Marked as Noise</p>
        </div>
        <div className="elite-card p-4 text-center">
          <p className="text-2xl font-bold text-slate-600">{stats.other || 0}</p>
          <p className="text-xs text-slate-500">Other Classifications</p>
        </div>
      </div>

      {/* Feedback List */}
      <div className="elite-card p-6">
        <h3 className="font-semibold text-stone-900 mb-4">Training Feedback History</h3>
        
        {feedback.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-8">No feedback submitted yet</p>
        ) : (
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {feedback.slice(0, 20).map(fb => (
              <div key={fb.feedback_id} className="p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs text-slate-500">{fb.original_class}</span>
                  <span className="text-xs">→</span>
                  {getClassBadge(fb.corrected_class)}
                  <span className="text-xs text-slate-400 ml-auto">
                    {new Date(fb.created_at).toLocaleDateString()}
                  </span>
                </div>
                <p className="text-xs text-slate-600 font-mono bg-white p-2 rounded truncate">
                  {fb.raw_text?.substring(0, 100)}...
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Main Page Component
const ParserTraining = () => {
  const { user, getAuthHeaders } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      // Use admin-ops/overview for unified stats
      const response = await axios.get(`${API}/admin-ops/overview`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      
      // Map admin-ops overview to parser stats format
      const data = response.data;
      setStats({
        pending_reviews: data.counts?.pending_reviews || 0,
        rules_count: data.downstream_counts?.parser_training?.total || 0,
        feedback_count: data.intake_counts?.total || 0
      });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!['admin', 'specialist', 'platform_owner', 'platform_admin', 'owner'].includes(user?.role)) {
    return (
      <div className="text-center py-12">
        <AlertCircle size={48} className="mx-auto text-red-500 mb-4" />
        <h2 className="text-xl font-bold text-stone-900 mb-2">Admin Access Required</h2>
        <p className="text-slate-500">This page is only available to administrators.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="parser-training">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-stone-900 flex items-center gap-3">
            <Brain className="text-amber-500" />
            Parser Training
          </h1>
          <p className="text-sm text-slate-500">
            Signal extraction and document parsing. Define what to detect and normalize from uploaded documents.
          </p>
        </div>
        
        {stats && (
          <div className="flex items-center gap-4 text-sm">
            <div className="text-center">
              <p className="text-lg font-bold text-amber-600">{stats.pending_reviews}</p>
              <p className="text-xs text-slate-500">Pending Reviews</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-slate-600">{stats.rules_count}</p>
              <p className="text-xs text-slate-500">Extraction Rules</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-slate-600">{stats.feedback_count}</p>
              <p className="text-xs text-slate-500">Training Items</p>
            </div>
          </div>
        )}
      </div>

      {/* Info Banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Target className="text-amber-500 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <h3 className="font-semibold text-amber-900">Extraction Focus</h3>
            <p className="text-sm text-amber-700 mt-1">
              Parser Training focuses on <strong>what to detect</strong> from documents. For classification logic and 
              method routing, go to <strong>Rules & Methods</strong> in the sidebar.
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <Tabs defaultValue="review" className="space-y-6">
        <TabsList>
          <TabsTrigger value="review" className="flex items-center gap-2">
            <AlertTriangle size={16} />
            Review Queue
            {stats?.pending_reviews > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-xs bg-red-500 text-white rounded-full">
                {stats.pending_reviews}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="extraction" className="flex items-center gap-2">
            <Filter size={16} />
            Extraction Rules
          </TabsTrigger>
          <TabsTrigger value="test" className="flex items-center gap-2">
            <Zap size={16} />
            Test
          </TabsTrigger>
          <TabsTrigger value="data" className="flex items-center gap-2">
            <FileText size={16} />
            Training Data
          </TabsTrigger>
        </TabsList>

        <TabsContent value="review">
          <ReviewQueue getAuthHeaders={getAuthHeaders} />
        </TabsContent>

        <TabsContent value="extraction">
          <RulesManager getAuthHeaders={getAuthHeaders} />
        </TabsContent>

        <TabsContent value="test">
          <ClassifierTester getAuthHeaders={getAuthHeaders} />
        </TabsContent>

        <TabsContent value="data">
          <TrainingFeedback getAuthHeaders={getAuthHeaders} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ParserTraining;
